import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Navigation translations with type safety and error handling
 * Provides centralized access to all Navigation-related translations with performance optimization
 */
export function useNavigationTranslations() {
  // Use the default translation namespace since navigation.json is merged into translation
  const { t } = useTranslation('translation');

  // Memoize translation functions to prevent re-renders
  const translations = useMemo(() => {
    // Helper functions for common translation patterns with type safety
    const getSidebarText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.${key}`, params || {}));
    };

    const getMainSectionText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.main.${key}`, params || {}));
    };

    const getManagementSectionText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.management.${key}`, params || {}));
    };

    const getTooltipText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.tooltips.${key}`, params || {}));
    };

    const getAriaText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.aria.${key}`, params || {}));
    };

    const getBrandingText = (key: string, params?: Record<string, any>): string => {
      return String(t(`navigation.sidebar.branding.${key}`, params || {}));
    };

    // Safe translation wrapper that always returns string
    const tSafe = (key: string, params?: Record<string, any>): string => {
      return String(t(key, params || {}));
    };

    // Helper for sidebar pin state tooltips
    const getPinStateTooltip = (pinState: 'open' | 'closed' | 'unpinned'): string => {
      switch (pinState) {
        case 'open':
          return getTooltipText('lockedOpen');
        case 'closed':
          return getTooltipText('lockedClosed');
        case 'unpinned':
          return getTooltipText('previewOnHover');
        default:
          return getTooltipText('previewOnHover');
      }
    };

    // Helper for aria labels with dynamic content
    const getSidebarAriaLabel = (pinState: string): string => {
      return getAriaText('sidebarMode', { mode: pinState });
    };

    return {
      // Direct translation function with type safety
      t: tSafe,

      // Category-specific helpers
      getSidebarText,
      getMainSectionText,
      getManagementSectionText,
      getTooltipText,
      getAriaText,
      getBrandingText,

      // Specialized helpers
      getPinStateTooltip,
      getSidebarAriaLabel,

      // Direct access to commonly used translations
      main: {
        sectionTitle: getMainSectionText('sectionTitle'),
        dashboard: getMainSectionText('dashboard'),
        products: getMainSectionText('products'),
        reports: getMainSectionText('reports'),
      },
      management: {
        sectionTitle: getManagementSectionText('sectionTitle'),
        uploadData: getManagementSectionText('uploadData'),
        documentation: getManagementSectionText('documentation'),
        team: getManagementSectionText('team'),
        settings: getManagementSectionText('settings'),
      },
      branding: {
        title: getBrandingText('title'),
      },
    };
  }, [t]);

  return translations;
}

export type NavigationTranslations = ReturnType<typeof useNavigationTranslations>;
